import { useState, useCallback, useEffect } from 'react'
import { Modal, Button, Text, Box, Image } from '@mantine/core'

const SLIDES_DATA = [
  {
    image: 'https://i.ibb.co/W4xkvZF3/1.png',
    title: 'Create Projects in Minutes',
    description:
      'Design your project with clear goals, activities, and assessments. ' +
      'Our planning tool keeps everything organized so you can focus on what matters: your students.',
  },
  {
    image: 'https://i.ibb.co/nq5QvS81/2.png',
    title: 'Use AI Support When You Need It',
    description:
      'Turn your ideas into structured project plans with optional AI assistance. ' +
      'Create project details, standards and skills, lessons and activities automatically.',
  },
  {
    image: 'https://i.ibb.co/gLfJbgC9/3.png',
    title: 'Find Inspiration in the Project Library',
    description:
      'Explore real-world projects created and tested by educators. ' +
      'Use them as inspiration or adapt the materials to fit your learning goals.',
  },
  {
    image: 'https://i.ibb.co/FLpD6327/4.png',
    title: 'Track Student Growth Over Time',
    description:
      'Follow each student\'s progress through a dynamic learning map. ' +
      'See how skills and competencies develop based on the evidence students share.',
  },
  {
    image: 'https://i.ibb.co/YTZTvrgS/5.png',
    title: 'Invite Students to Join and Participate',
    description:
      'Bring students into the project with a simple invitation. ' +
      'Organize teams, guide their work, and provide feedback in one place.',
  },
]

interface OnboardingModalProps {
  opened: boolean
  onComplete: () => void
}

export const OnboardingModal = ({ opened, onComplete }: OnboardingModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [maxVisitedSlide, setMaxVisitedSlide] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)

  // Preload ALL images when modal opens - ensures instant transitions
  useEffect(() => {
    if (opened && !imagesLoaded) {
      let loadedCount = 0
      const totalImages = SLIDES_DATA.length

      SLIDES_DATA.forEach(slide => {
        const img = new window.Image()
        img.onload = () => {
          loadedCount++
          if (loadedCount === totalImages) {
            setImagesLoaded(true)
          }
        }
        img.src = slide.image
      })
    }
  }, [opened, imagesLoaded])

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setCurrentSlide(0)
      setMaxVisitedSlide(0)
    }
  }, [opened])

  const currentData = SLIDES_DATA[currentSlide]
  const isLastSlide = currentSlide === SLIDES_DATA.length - 1

  const handleNext = useCallback(() => {
    if (isLastSlide) {
      onComplete()
    } else {
      const nextSlide = currentSlide + 1
      setCurrentSlide(nextSlide)
      setMaxVisitedSlide(prev => Math.max(prev, nextSlide))
    }
  }, [currentSlide, isLastSlide, onComplete])

  const handleIndicatorClick = useCallback((index: number) => {
    if (index <= maxVisitedSlide) {
      setCurrentSlide(index)
    }
  }, [maxVisitedSlide])

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      overlayProps={{ blur: 8, opacity: 0.55 }}
      padding={0}
      radius="lg"
      size="lg"
      styles={{
        content: {
          boxShadow:
            '0 11px 15px -7px rgba(0,0,0,0.2), ' +
            '0 24px 38px 3px rgba(0,0,0,0.14), ' +
            '0 9px 46px 8px rgba(0,0,0,0.12)',
        },
      }}
    >
      <Box
        sx={theme => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          // Mobile-first: smaller padding
          padding: '24px 20px',
          // Tablet+
          [theme.fn.largerThan('xs')]: {
            padding: '32px 32px',
          },
          // Desktop
          [theme.fn.largerThan('sm')]: {
            padding: '40px 48px',
          },
        })}
      >
        {/* Image Container - fixed height prevents layout shift */}
        <Box
          sx={theme => ({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Mobile-first
            height: 180,
            marginBottom: 20,
            [theme.fn.largerThan('xs')]: {
              height: 240,
              marginBottom: 24,
            },
            [theme.fn.largerThan('sm')]: {
              height: 280,
              marginBottom: 28,
            },
          })}
        >
          <Image
            src={currentData.image}
            alt={currentData.title}
            fit="contain"
            height="100%"
            width="auto"
            styles={{
              root: { height: '100%' },
              figure: { height: '100%' },
              imageWrapper: { height: '100%' },
            }}
          />
        </Box>

        {/* Text Content - fixed height prevents layout shift */}
        <Box
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Mobile-first: taller to fit wrapped text
            height: 130,
            [theme.fn.largerThan('xs')]: {
              height: 120,
            },
            [theme.fn.largerThan('sm')]: {
              height: 110,
            },
          })}
        >
          <Text
            sx={theme => ({
              fontWeight: 600,
              color: theme.colors.dark[8],
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              // Mobile-first
              fontSize: 20,
              marginBottom: 10,
              [theme.fn.largerThan('xs')]: {
                fontSize: 23,
                marginBottom: 12,
              },
              [theme.fn.largerThan('sm')]: {
                fontSize: 26,
                marginBottom: 14,
              },
            })}
          >
            {currentData.title}
          </Text>
          <Text
            sx={theme => ({
              color: theme.colors.gray[7],
              lineHeight: 1.55,
              fontWeight: 400,
              // Mobile-first
              fontSize: 14,
              maxWidth: 300,
              [theme.fn.largerThan('xs')]: {
                fontSize: 15,
                maxWidth: 420,
              },
              [theme.fn.largerThan('sm')]: {
                fontSize: 16,
                maxWidth: 520,
              },
            })}
          >
            {currentData.description}
          </Text>
        </Box>

        {/* Indicators */}
        <Box
          sx={theme => ({
            display: 'flex',
            justifyContent: 'center',
            // Mobile-first
            gap: 10,
            marginTop: 20,
            marginBottom: 18,
            [theme.fn.largerThan('xs')]: {
              gap: 12,
              marginTop: 24,
              marginBottom: 20,
            },
            [theme.fn.largerThan('sm')]: {
              marginTop: 28,
              marginBottom: 24,
            },
          })}
        >
          {SLIDES_DATA.map((_, index) => {
            const isVisited = index <= maxVisitedSlide
            const isActive = index <= currentSlide
            return (
              <Box
                key={index}
                component="button"
                type="button"
                onClick={() => handleIndicatorClick(index)}
                sx={theme => ({
                  // Mobile-first
                  width: 10,
                  height: 10,
                  [theme.fn.largerThan('sm')]: {
                    width: 12,
                    height: 12,
                  },
                  borderRadius: '50%',
                  border: 'none',
                  padding: 0,
                  backgroundColor: isActive ? theme.colors.teal[6] : theme.colors.teal[2],
                  transition: 'all 200ms ease',
                  cursor: isVisited ? 'pointer' : 'default',
                  '&:hover': isVisited ? { transform: 'scale(1.15)' } : {},
                })}
                aria-label={`Go to slide ${index + 1}`}
              />
            )
          })}
        </Box>

        {/* Button */}
        <Button
          color="teal"
          onClick={handleNext}
          sx={theme => ({
            fontWeight: 600,
            // Mobile-first
            borderRadius: 20,
            paddingLeft: 28,
            paddingRight: 28,
            height: 42,
            fontSize: 15,
            [theme.fn.largerThan('xs')]: {
              borderRadius: 22,
              paddingLeft: 34,
              paddingRight: 34,
              height: 44,
              fontSize: 16,
            },
            [theme.fn.largerThan('sm')]: {
              paddingLeft: 40,
              paddingRight: 40,
              height: 48,
              fontSize: 17,
            },
          })}
        >
          {isLastSlide ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Modal>
  )
}
